import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { checkinSteps, clinicalNoteSections, defaultPracticeSettings } from 'constants';
import { useRecoilValue } from 'recoil';

import { pm } from 'lib/helpers/utility';
import { updatePractice } from 'lib/hooks/queries/practice/usePractice';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Allowed from 'components/shared/Permissions/Allowed';
import ToggleSwitch from 'components/shared/ToggleSwitch/ToggleSwitch';
import state from 'components/state';

import switchItems from './lib/settingsTabs';

const Settings = () => {
  const { practice } = useOutletContext();
  const permissions = useRecoilValue(state.permissions);
  const updateDisabled = !pm(permissions, 'practice.update');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const lastSegment = location.pathname.split('/').pop();

  useEffect(() => {
    if (lastSegment == 'settings') {
      navigate('./forms');
    }
  }, []);

  const checkActive = () => {
    return switchItems.find((i) => i?.value === lastSegment)?.value;
  };

  const updatePracticeMutation = useCustomMutation({
    mutationFn: (updatedPractice) =>
      updatePractice(navigate, {
        practice_id: practice.id,
        fields: updatedPractice
      }),
    successMessage: 'Settings was updated successfully!',
    errorMessage: 'There was an error updating settings.',
    onSuccess: () => {
      queryClient.invalidateQueries(['practice']);
    }
  });

  const handleChange = async (index, val) => {
    const display_settings = practice?.display_settings;
    let fields = {};

    if (['surcharge', 'care_credit', 'emails_enabled', 'texts_enabled'].includes(index?.name)) {
      fields = { [index?.name]: index?.state };
    } else if (index === 'checkin_steps') {
      fields = { checkin_steps: val };
    } else if (index === 'self_scheduling') {
      fields = { self_scheduling: val };
    } else {
      display_settings[index] = val;
      fields = { display_settings };
    }

    await updatePracticeMutation.mutateAsync(fields);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden px-4 pb-4">
      <Allowed requiredPermissions="practice.update">
        <div className="flex pb-4">
          <ToggleSwitch
            items={switchItems}
            onClick={(i) => {
              navigate(i?.value);
            }}
            active={checkActive()}
          />
        </div>
      </Allowed>
      <Outlet
        context={{
          practice,
          defaultPracticeSettings,
          handleChange,
          updateDisabled,
          clinicalNoteSections,
          checkinSteps: { ...checkinSteps, ...practice?.checkin_steps }
        }}
      />
    </div>
  );
};

export default withErrorBoundary(Settings);
